/**
 * Producer certification quiz — 8 timed interactive questions.
 * Tests all producer controls: go live, go dark, focus, scores, leaderboard,
 * heat switching, attempt management, overlay style.
 */

//  — some onStateChange callbacks don't use state
import { useState, useEffect } from "react";
import { QuizEngine, type QuizQuestion } from "./QuizEngine";
import {
  MockProducerScreen,
  type MockProducerRider,
  type MockProducerHeat,
  type ProducerLiveState,
} from "./MockProducerScreen";

// ─── Shared mock data ────────────────────────────────────────

const HEATS: MockProducerHeat[] = [
  { id: "h1", name: "Round 1", division: "Men's Pro" },
  { id: "h2", name: "Semi-Finals", division: "Men's Pro" },
  { id: "h3", name: "Finals", division: "Men's Pro" },
  { id: "h4", name: "Round 1", division: "Women's Open" },
  { id: "h5", name: "Finals", division: "Women's Open" },
  { id: "h6", name: "Round 1", division: "Juniors" },
];

const BASE_RIDERS: Record<string, MockProducerRider[]> = {
  h1: [
    {
      id: "r1", name: "Harley Clifford", nickname: "The King", attempts: 3, currentAttempt: 2,
      judgeScores: { "Britta": { 1: 8.5, 2: 7.9 }, "J2": { 1: 8.2, 2: 7.5 }, "J3": { 1: null, 2: null } },
      finals: { 1: 8.4, 2: 7.7, 3: null },
    },
    {
      id: "r2", name: "Nic Rapa", nickname: "Raptor", attempts: 3, currentAttempt: 3,
      judgeScores: { "Britta": { 1: 7.0, 2: 6.8, 3: 8.3 }, "J2": { 1: 7.2, 2: 7.0, 3: 8.1 }, "J3": { 1: null, 2: null, 3: null } },
      finals: { 1: 7.1, 2: 6.9, 3: 8.2 },
    },
    {
      id: "r3", name: "Steel Lafferty", nickname: "Steele", attempts: 3, currentAttempt: 1,
      judgeScores: { "Britta": { 1: 6.5 }, "J2": { 1: 6.8 }, "J3": { 1: null } },
      finals: { 1: 6.7, 2: null, 3: null },
    },
  ],
  h2: [
    { id: "r1", name: "Harley Clifford", nickname: "The King", attempts: 3, currentAttempt: 0, judgeScores: {}, finals: {} },
    { id: "r2", name: "Nic Rapa", nickname: "Raptor", attempts: 3, currentAttempt: 0, judgeScores: {}, finals: {} },
  ],
  h3: [
    { id: "r1", name: "Harley Clifford", nickname: "The King", attempts: 3, currentAttempt: 0, judgeScores: {}, finals: {} },
    { id: "r2", name: "Nic Rapa", nickname: "Raptor", attempts: 3, currentAttempt: 0, judgeScores: {}, finals: {} },
    { id: "r4", name: "Bentley Bland", attempts: 3, currentAttempt: 0, judgeScores: {}, finals: {} },
  ],
  h4: [
    {
      id: "r5", name: "Dallas Friday", nickname: "D-Fri", attempts: 3, currentAttempt: 3,
      judgeScores: { "Britta": { 1: 8.0, 2: 7.5, 3: 8.8 }, "J2": { 1: 8.3, 2: 7.8, 3: 9.0 }, "J3": { 1: 7.9, 2: 7.2, 3: 8.5 } },
      finals: { 1: 8.1, 2: 7.5, 3: 8.8 },
    },
    {
      id: "r6", name: "Amber Wing", nickname: "Wings", attempts: 3, currentAttempt: 2,
      judgeScores: { "Britta": { 1: 7.2, 2: 8.1 }, "J2": { 1: 7.5, 2: 8.4 }, "J3": { 1: 7.0, 2: 7.9 } },
      finals: { 1: 7.2, 2: 8.1, 3: null },
    },
    {
      id: "r7", name: "Meagan Ethell", nickname: "Mega", attempts: 3, currentAttempt: 1,
      judgeScores: { "Britta": { 1: 6.5 }, "J2": { 1: 6.8 }, "J3": { 1: 7.1 } },
      finals: { 1: 6.8, 2: null, 3: null },
    },
  ],
  h5: [
    { id: "r5", name: "Dallas Friday", nickname: "D-Fri", attempts: 3, currentAttempt: 0, judgeScores: {}, finals: {} },
    { id: "r6", name: "Amber Wing", nickname: "Wings", attempts: 3, currentAttempt: 0, judgeScores: {}, finals: {} },
  ],
  h6: [
    {
      id: "r8", name: "Jake Thompson", nickname: "JT", attempts: 2, currentAttempt: 1,
      judgeScores: { "Britta": { 1: 6.0 }, "J2": { 1: 5.5 } },
      finals: { 1: 5.8, 2: null },
    },
    { id: "r9", name: "Mia Rodriguez", nickname: "Mimi", attempts: 2, currentAttempt: 0, judgeScores: {}, finals: {} },
  ],
};

const OFF_AIR: ProducerLiveState = {
  isLive: false,
  liveHeatId: null,
  focusRiderId: null,
  scoresRiderId: null,
  showLeaderboard: false,
  showHeatScores: false,
  showRiderScores: false,
  showCurrentHeat: true,
  overlayStyle: "steel",
};

// ─── Q1: Go Live on a specific heat ─────────────────────────

function Q1Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  return (
    <MockProducerScreen
      heats={HEATS}
      ridersByHeat={BASE_RIDERS}
      initialState={OFF_AIR}
      onStateChange={(state, action) => {
        if (action === "go-live" && state.liveHeatId === "h3") {
          onComplete(true);
        }
      }}
    />
  );
}

// ─── Q2: Focus a specific rider ─────────────────────────────

function Q2Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  return (
    <MockProducerScreen
      heats={HEATS}
      ridersByHeat={BASE_RIDERS}
      initialState={{ ...OFF_AIR, isLive: true, liveHeatId: "h1" }}
      onStateChange={(_state, action) => {
        if (action === "focus-r3") {
          onComplete(true);
        }
      }}
    />
  );
}

// ─── Q3: Show the leaderboard ───────────────────────────────

function Q3Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  return (
    <MockProducerScreen
      heats={HEATS}
      ridersByHeat={BASE_RIDERS}
      initialState={{ ...OFF_AIR, isLive: true, liveHeatId: "h1" }}
      onStateChange={(_state, action) => {
        if (action === "show-leaderboard") {
          onComplete(true);
        }
      }}
    />
  );
}

// ─── Q4: Go Dark ────────────────────────────────────────────

function Q4Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  return (
    <MockProducerScreen
      heats={HEATS}
      ridersByHeat={BASE_RIDERS}
      initialState={{
        ...OFF_AIR,
        isLive: true,
        liveHeatId: "h4",
        showLeaderboard: true,
        showHeatScores: true,
        focusRiderId: "r5",
      }}
      onStateChange={(_state, action) => {
        if (action === "go-dark") {
          onComplete(true);
        }
      }}
    />
  );
}

// ─── Q5: Advance a rider's attempt ──────────────────────────

function Q5Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  return (
    <MockProducerScreen
      heats={HEATS}
      ridersByHeat={BASE_RIDERS}
      initialState={{ ...OFF_AIR, isLive: true, liveHeatId: "h1" }}
      onAttemptChange={(_heatId, riderId, newAttempt, direction) => {
        if (riderId === "r3" && direction === "advance" && newAttempt === 2) {
          onComplete(true);
        }
      }}
    />
  );
}

// ─── Q6: Switch heat while live ─────────────────────────────

function Q6Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  return (
    <MockProducerScreen
      heats={HEATS}
      ridersByHeat={BASE_RIDERS}
      initialState={{ ...OFF_AIR, isLive: true, liveHeatId: "h1" }}
      onStateChange={(state, action) => {
        if (action === "switch-heat" && state.liveHeatId === "h4") {
          onComplete(true);
        }
      }}
      onHeatSelect={(heatId) => {
        // Also fires via onStateChange for live switches
        void heatId;
      }}
    />
  );
}

// ─── Q7: Change overlay style ───────────────────────────────

function Q7Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  return (
    <MockProducerScreen
      heats={HEATS}
      ridersByHeat={BASE_RIDERS}
      initialState={{ ...OFF_AIR, isLive: true, liveHeatId: "h1", overlayStyle: "steel" }}
      onStateChange={(_state, action) => {
        if (action === "style-neon") {
          onComplete(true);
        }
      }}
    />
  );
}

// ─── Q8: Full sequence — go live, focus, show scores, go dark

function Q8Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  const [step, setStep] = useState(0);
  const steps = [
    "Go Live on Women's Open Finals",
    "Focus Dallas Friday",
    "Show the Leaderboard",
    "Go Dark",
  ];

  return (
    <div>
      {/* Progress indicator */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 12,
          padding: "8px 12px",
          background: "var(--c-surface-2)",
          border: "1px solid var(--c-border)",
        }}
      >
        {steps.map((s, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11,
              color:
                i < step
                  ? "#22c55e"
                  : i === step
                    ? "var(--c-text)"
                    : "var(--c-text-dim)",
              fontWeight: i === step ? 700 : 400,
            }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 9,
                fontWeight: 700,
                background:
                  i < step
                    ? "#22c55e"
                    : i === step
                      ? "var(--c-accent)"
                      : "var(--c-surface-2)",
                color: i <= step ? "#fff" : "var(--c-text-dim)",
                border: `1px solid ${i < step ? "#22c55e" : i === step ? "var(--c-accent)" : "var(--c-border)"}`,
              }}
            >
              {i < step ? "\u2713" : i + 1}
            </span>
            {s}
          </div>
        ))}
      </div>

      <MockProducerScreen
        heats={HEATS}
        ridersByHeat={BASE_RIDERS}
        initialState={OFF_AIR}
        onStateChange={(state, action) => {
          if (step === 0 && action === "go-live" && state.liveHeatId === "h5") {
            setStep(1);
          }
          if (step === 1 && action === "focus-r5") {
            setStep(2);
          }
          if (step === 2 && action === "show-leaderboard") {
            setStep(3);
          }
          if (step === 3 && action === "go-dark") {
            onComplete(true);
          }
        }}
      />
    </div>
  );
}

// ─── Q9: Rapid actions — switch heat, advance, leaderboard ──

function Q9Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  const [step, setStep] = useState(0);
  const steps = [
    "Switch to Men's Pro Finals",
    "Advance Bentley Bland by 1",
    "Show the Leaderboard",
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 12,
          padding: "8px 12px",
          background: "var(--c-surface-2)",
          border: "1px solid var(--c-border)",
        }}
      >
        {steps.map((s, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11,
              color:
                i < step
                  ? "#22c55e"
                  : i === step
                    ? "var(--c-text)"
                    : "var(--c-text-dim)",
              fontWeight: i === step ? 700 : 400,
            }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 9,
                fontWeight: 700,
                background:
                  i < step
                    ? "#22c55e"
                    : i === step
                      ? "var(--c-accent)"
                      : "var(--c-surface-2)",
                color: i <= step ? "#fff" : "var(--c-text-dim)",
                border: `1px solid ${i < step ? "#22c55e" : i === step ? "var(--c-accent)" : "var(--c-border)"}`,
              }}
            >
              {i < step ? "\u2713" : i + 1}
            </span>
            {s}
          </div>
        ))}
      </div>

      <MockProducerScreen
        heats={HEATS}
        ridersByHeat={BASE_RIDERS}
        initialState={{ ...OFF_AIR, isLive: true, liveHeatId: "h1" }}
        onStateChange={(state, action) => {
          if (step === 0 && action === "switch-heat" && state.liveHeatId === "h3") {
            setStep(1);
          }
          if (step === 2 && action === "show-leaderboard") {
            onComplete(true);
          }
        }}
        onAttemptChange={(_heatId, riderId, _newAttempt, direction) => {
          if (step === 1 && riderId === "r4" && direction === "advance") {
            setStep(2);
          }
        }}
      />
    </div>
  );
}

// ─── Q10: Full event flow — advance all, scores come in, edit finals, go live

function Q10Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  const [step, setStep] = useState(0);
  const [ridersState, setRidersState] = useState<Record<string, MockProducerRider[]>>({
    h1: [
      { id: "r1", name: "Harley Clifford", nickname: "The King", attempts: 3, currentAttempt: 2,
        judgeScores: { "Britta": { 1: 8.5, 2: 7.9 }, "J2": { 1: 8.2, 2: 7.5 } }, finals: { 1: 8.4, 2: 7.7, 3: null } },
      { id: "r3", name: "Steel Lafferty", nickname: "Steele", attempts: 3, currentAttempt: 2,
        judgeScores: { "Britta": { 1: 6.5, 2: 7.1 }, "J2": { 1: 6.8, 2: 7.3 } }, finals: { 1: 6.7, 2: 7.2, 3: null } },
      { id: "r4", name: "Bentley Bland", attempts: 3, currentAttempt: 2,
        judgeScores: { "Britta": { 1: 7.0, 2: 8.2 }, "J2": { 1: 7.3, 2: 8.0 } }, finals: { 1: 7.2, 2: 8.1, 3: null } },
    ],
    h3: [],
  });
  const [allAdvanced, setAllAdvanced] = useState(false);
  let scoresArrived = false;

  const steps = [
    "Advance all 3 riders to attempt 3",
    "Wait for scores to come in...",
    "Edit Finals — add Harley & Bentley",
    "Switch to Finals",
  ];

  // Check if all riders are at attempt 3
  useEffect(() => {
    const h1Riders = ridersState.h1 ?? [];
    const allAt3 = h1Riders.length === 3 && h1Riders.every((r) => r.currentAttempt >= 3);
    if (allAt3 && !allAdvanced) {
      setAllAdvanced(true);
      setStep(1);
      // Scores arrive 0.5s after all at attempt 3
      setTimeout(() => {
        setRidersState((prev) => ({
          ...prev,
          h1: (prev.h1 ?? []).map((r) => ({
            ...r,
            judgeScores: {
              ...r.judgeScores,
              "Britta": { ...(r.judgeScores?.["Britta"] ?? {}), 3: Math.round((7 + Math.random() * 3) * 10) / 10 },
              "J2": { ...(r.judgeScores?.["J2"] ?? {}), 3: Math.round((7 + Math.random() * 3) * 10) / 10 },
            },
            finals: {
              ...r.finals,
              3: Math.round((7 + Math.random() * 2.5) * 10) / 10,
            },
          })),
        }));
        scoresArrived = true; void scoresArrived;
        setStep(2);
      }, 500);
    }
  }, [ridersState, allAdvanced]);

  // Check if Finals has Harley and Bentley
  useEffect(() => {
    if (step < 2) return;
    const finalsRiders = ridersState.h3 ?? [];
    const hasHarley = finalsRiders.some((r) => r.id === "r1");
    const hasBentley = finalsRiders.some((r) => r.id === "r4");
    if (hasHarley && hasBentley && step === 2) {
      setStep(3);
    }
  }, [ridersState, step]);

  const allAvailable = [
    { id: "r1", name: "Harley Clifford", nickname: "The King" },
    { id: "r3", name: "Steel Lafferty", nickname: "Steele" },
    { id: "r4", name: "Bentley Bland" },
  ];

  return (
    <div>
      <div style={{
        display: "flex", gap: 8, marginBottom: 12, padding: "8px 12px",
        background: "var(--c-surface-2)", border: "1px solid var(--c-border)", flexWrap: "wrap",
      }}>
        {steps.map((s, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 6, fontSize: 11,
            color: i < step ? "#22c55e" : i === step ? "var(--c-text)" : "var(--c-text-dim)",
            fontWeight: i === step ? 700 : 400,
          }}>
            <span style={{
              width: 18, height: 18, borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 9, fontWeight: 700,
              background: i < step ? "#22c55e" : i === step ? "var(--c-accent)" : "var(--c-surface-2)",
              color: i <= step ? "#fff" : "var(--c-text-dim)",
              border: `1px solid ${i < step ? "#22c55e" : i === step ? "var(--c-accent)" : "var(--c-border)"}`,
            }}>
              {i < step ? "\u2713" : i + 1}
            </span>
            {s}
          </div>
        ))}
      </div>

      <MockProducerScreen
        heats={[
          { id: "h1", name: "Round 1", division: "Men's Pro" },
          { id: "h3", name: "Finals", division: "Men's Pro" },
        ]}
        ridersByHeat={ridersState}
        availableRiders={allAvailable}
        initialState={{ ...OFF_AIR, isLive: true, liveHeatId: "h1" }}
        onAttemptChange={(heatId, riderId, newAttempt, direction) => {
          if (heatId === "h1" && direction === "advance") {
            setRidersState((prev) => ({
              ...prev,
              h1: (prev.h1 ?? []).map((r) =>
                r.id === riderId ? { ...r, currentAttempt: newAttempt } : r
              ),
            }));
          }
        }}
        onRidersChanged={(heatId, riderIds) => {
          if (heatId === "h3") {
            // Update Finals riders
            setRidersState((prev) => {
              const newRiders = riderIds.map((id) => {
                const existing = prev.h3?.find((r) => r.id === id);
                if (existing) return existing;
                const source = allAvailable.find((a) => a.id === id);
                return {
                  id, name: source?.name ?? id, nickname: source?.nickname,
                  attempts: 3, currentAttempt: 0,
                  judgeScores: {}, finals: {},
                } as MockProducerRider;
              });
              return { ...prev, h3: newRiders };
            });
          }
        }}
        onStateChange={(state, action) => {
          if (step === 3 && (action === "go-live" || action === "switch-heat") && state.liveHeatId === "h3") {
            onComplete(true);
          }
        }}
      />
    </div>
  );
}

// ─── Quiz definition ────────────────────────────────────────

const PRODUCER_QUESTIONS: QuizQuestion[] = [
  {
    id: "go-live",
    title: "Go Live",
    instruction: "Go Live on Men's Pro Finals",
    timeLimit: 10,
    render: (props) => <Q1Render {...props} />,
  },
  {
    id: "focus-rider",
    title: "Focus a Rider",
    instruction: "Focus Steel Lafferty on the broadcast",
    timeLimit: 8,
    render: (props) => <Q2Render {...props} />,
  },
  {
    id: "show-leaderboard",
    title: "Show Leaderboard",
    instruction: "Show the leaderboard on the overlay",
    timeLimit: 6,
    render: (props) => <Q3Render {...props} />,
  },
  {
    id: "go-dark",
    title: "Go Dark",
    instruction: "Take the overlay off-air",
    timeLimit: 6,
    render: (props) => <Q4Render {...props} />,
  },
  {
    id: "advance-attempt",
    title: "Advance Attempt",
    instruction: "Advance Steel Lafferty to attempt 2 (currently on 1)",
    timeLimit: 10,
    render: (props) => <Q5Render {...props} />,
  },
  {
    id: "switch-heat",
    title: "Switch Heat While Live",
    instruction: "Switch the live broadcast to Women's Open Round 1",
    timeLimit: 10,
    render: (props) => <Q6Render {...props} />,
  },
  {
    id: "change-style",
    title: "Change Overlay Style",
    instruction: "Change the overlay style to Neon",
    timeLimit: 8,
    render: (props) => <Q7Render {...props} />,
  },
  {
    id: "full-sequence",
    title: "Full Broadcast Sequence",
    instruction:
      "Complete the full sequence: Go Live on Women's Open Finals → Focus Dallas Friday → Show Leaderboard → Go Dark",
    timeLimit: 30,
    render: (props) => <Q8Render {...props} />,
  },
  {
    id: "rapid-actions",
    title: "Rapid Actions",
    instruction: "Switch to Men's Pro Finals → Advance Bentley Bland by 1 → Show Leaderboard",
    timeLimit: 15,
    render: (props) => <Q9Render {...props} />,
  },
  {
    id: "full-event",
    title: "Run an Event",
    instruction: "Advance all riders to attempt 3, wait for scores, edit Finals to add Harley & Bentley, then Go Live on Finals",
    timeLimit: 45,
    render: (props) => <Q10Render {...props} />,
  },
];

// ─── Export ─────────────────────────────────────────────────

interface ProducerQuizProps {
  onPass: () => void;
  onClose: () => void;
}

export function ProducerQuiz({ onPass, onClose }: ProducerQuizProps) {
  return (
    <QuizEngine
      title="Producer Certification Quiz"
      questions={PRODUCER_QUESTIONS}
      onPass={onPass}
      onClose={onClose}
    />
  );
}
